import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import { usePro } from '../contexts/ProContext';
import ProGate from './ProGate';
import { HiOutlineBell, HiOutlineMail, HiOutlineTrash, HiOutlinePlus } from 'react-icons/hi';

const ALERT_TYPES = [
  { value: 'apr_above', label: 'APR rises above', icon: '\u2191' },
  { value: 'apr_below', label: 'APR drops below', icon: '\u2193' },
  { value: 'tvl_drop', label: 'TVL drops by %', icon: '\u26A0' },
  { value: 'risk_change', label: 'Risk tier changes', icon: '\u{1F6E1}' },
  { value: 'new_pool', label: 'New pool for token', icon: '\u2728' },
];

export default function AlertsProPanel({ pools }) {
  const { colors, dark } = useTheme();
  const isMobile = useIsMobile();
  const { session } = usePro();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // History of triggered alerts (future: fetch from API)
  // const [history, setHistory] = useState([]);

  // Form state
  const [form, setForm] = useState({
    type: 'apr_above',
    poolId: '',
    poolName: '',
    threshold: '',
    email: '',
    webhookUrl: '',
  });

  const fetchRules = useCallback(async () => {
    if (!session?.token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/alerts/subscribe', {
        headers: { Authorization: `Bearer ${session.token}` },
      });
      if (!res.ok) throw new Error('API unavailable');
      const data = await res.json();
      setRules(data.rules || []);
    } catch {
      // Offline or API unavailable — use empty
    }
    setLoading(false);
  }, [session?.token]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const createRule = async () => {
    if (!form.type) return;
    if (!session?.token) {
      // Demo mode: add rule locally
      const demoRule = {
        id: Date.now().toString(),
        ...form,
        threshold: form.threshold ? parseFloat(form.threshold) : undefined,
        triggerCount: 0,
      };
      setRules(prev => [...prev, demoRule]);
      setShowCreate(false);
      setForm({ type: 'apr_above', poolId: '', poolName: '', threshold: '', email: '', webhookUrl: '' });
      toast.success('Alert rule created (local demo)');
      return;
    }

    // Validate threshold is required for apr_above, apr_below, tvl_drop
    const needsThreshold = ['apr_above', 'apr_below', 'tvl_drop'].includes(form.type);
    if (needsThreshold && (!form.threshold || isNaN(parseFloat(form.threshold)))) {
      toast.error('Threshold is required for this alert type');
      return;
    }

    try {
      const res = await fetch('/api/alerts/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.token}`,
        },
        body: JSON.stringify({
          ...form,
          threshold: form.threshold ? parseFloat(form.threshold) : undefined,
        }),
      });
      if (res.ok) {
        toast.success('Alert rule created');
        setShowCreate(false);
        setForm({ type: 'apr_above', poolId: '', poolName: '', threshold: '', email: '', webhookUrl: '' });
        fetchRules();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || 'Failed to create alert rule');
      }
    } catch (err) {
      toast.error('Failed to create alert rule. Please try again.');
    }
  };

  const deleteRule = async (ruleId) => {
    if (!session?.token) {
      // Demo mode: remove locally
      setRules(prev => prev.filter(r => r.id !== ruleId));
      toast.success('Alert rule deleted');
      return;
    }
    try {
      const res = await fetch('/api/alerts/subscribe', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.token}`,
        },
        body: JSON.stringify({ ruleId }),
      });
      if (res.ok) {
        toast.success('Alert rule deleted');
      } else {
        toast.error('Failed to delete alert rule');
      }
      fetchRules();
    } catch {
      toast.error('Failed to delete alert rule');
    }
  };

  const content = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ maxWidth: 800, margin: '0 auto', padding: isMobile ? '16px 12px' : '24px' }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
      }}>
        <div>
          <div style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: isMobile ? 24 : 28,
            color: colors.textPrimary,
          }}>
            Pro Alerts
          </div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: colors.textSecondary,
          }}>
            Email & webhook notifications for your DeFi positions
          </div>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            color: '#FFFFFF',
            background: colors.green,
            border: 'none',
            borderRadius: 8,
            padding: '8px 16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <HiOutlinePlus size={14} />
          New Alert
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div style={{
          background: colors.surface,
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          padding: 20,
          marginBottom: 16,
        }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            fontWeight: 600,
            color: colors.labelLight,
            letterSpacing: 1,
            marginBottom: 12,
          }}>
            CREATE ALERT RULE
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            {/* Alert type */}
            <div>
              <label style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>
                Type
              </label>
              <select
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 12,
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  background: colors.bg,
                  color: colors.textPrimary,
                }}
              >
                {ALERT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                ))}
              </select>
            </div>

            {/* Pool selector */}
            <div>
              <label style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>
                Pool
              </label>
              <select
                value={form.poolId}
                onChange={e => {
                  const p = pools?.find(p => p.id === e.target.value || String(p.id) === e.target.value);
                  setForm(f => ({ ...f, poolId: e.target.value, poolName: p?.name || '' }));
                }}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 12,
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  background: colors.bg,
                  color: colors.textPrimary,
                }}
              >
                <option value="">Any pool</option>
                {(pools || []).slice(0, 100).map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.protocol})</option>
                ))}
              </select>
            </div>

            {/* Threshold */}
            <div>
              <label style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>
                Threshold
              </label>
              <input
                type="number"
                value={form.threshold}
                onChange={e => setForm(f => ({ ...f, threshold: e.target.value }))}
                placeholder="e.g. 5.0"
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 12,
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  background: colors.bg,
                  color: colors.textPrimary,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Email */}
            <div>
              <label style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>
                Email (optional)
              </label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="you@example.com"
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 12,
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  background: colors.bg,
                  color: colors.textPrimary,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Webhook URL */}
            <div style={{ gridColumn: isMobile ? 'auto' : '1 / -1' }}>
              <label style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>
                Webhook URL (Discord/Telegram, optional)
              </label>
              <input
                type="url"
                value={form.webhookUrl}
                onChange={e => setForm(f => ({ ...f, webhookUrl: e.target.value }))}
                placeholder="https://discord.com/api/webhooks/..."
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 12,
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  background: colors.bg,
                  color: colors.textPrimary,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowCreate(false)}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                color: colors.textSecondary,
                background: 'transparent',
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                padding: '6px 14px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={createRule}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                fontWeight: 600,
                color: '#FFFFFF',
                background: colors.green,
                border: 'none',
                borderRadius: 6,
                padding: '6px 14px',
                cursor: 'pointer',
              }}
            >
              Create Alert
            </button>
          </div>
        </div>
      )}

      {/* Rules list */}
      <div style={{
        background: colors.surface,
        borderRadius: 12,
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '12px 16px',
          background: dark ? 'rgba(255,255,255,0.03)' : '#FAFAFA',
          borderBottom: `1px solid ${colors.border}`,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          fontWeight: 600,
          color: colors.labelLight,
          letterSpacing: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <HiOutlineBell size={12} />
          ACTIVE RULES ({rules.length})
        </div>

        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: colors.labelMuted }}>
            Loading...
          </div>
        ) : rules.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center' }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: colors.textSecondary }}>
              No alert rules yet
            </div>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.labelMuted, marginTop: 4 }}>
              Create your first alert to get notified by email or webhook
            </div>
          </div>
        ) : (
          rules.map(rule => {
            const typeInfo = ALERT_TYPES.find(t => t.value === rule.type) || { label: rule.type, icon: '\u{1F514}' };
            return (
              <div key={rule.id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.04)' : '#F4F5F6'}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 16 }}>{typeInfo.icon}</span>
                  <div>
                    <div style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 12,
                      color: colors.textPrimary,
                    }}>
                      {typeInfo.label}{rule.threshold ? ` ${rule.threshold}%` : ''}
                    </div>
                    <div style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10,
                      color: colors.labelMuted,
                      display: 'flex',
                      gap: 8,
                      marginTop: 2,
                    }}>
                      {rule.poolName && <span>{rule.poolName}</span>}
                      {rule.email && <span><HiOutlineMail size={10} style={{ verticalAlign: 'middle' }} /> {rule.email}</span>}
                      {rule.triggerCount > 0 && <span>Triggered {rule.triggerCount}x</span>}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => deleteRule(rule.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: colors.textSecondary,
                    cursor: 'pointer',
                    padding: 4,
                  }}
                >
                  <HiOutlineTrash size={16} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </motion.div>
  );

  return (
    <ProGate feature="advanced email & webhook alerts">
      {content}
    </ProGate>
  );
}
